import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  AlertTriangle, FileText, Shield, Clock, CheckCircle2, XCircle,
  Plus, Loader2, FlaskConical, Home, Users, ChevronDown
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const INCIDENT_TYPES = [
  { value: 'rule_violation', label: 'Rule Violation' },
  { value: 'altercation', label: 'Altercation' },
  { value: 'property_damage', label: 'Property Damage' },
  { value: 'substance_use', label: 'Substance Use' },
  { value: 'curfew_breach', label: 'Curfew Breach' },
  { value: 'unauthorized_guest', label: 'Unauthorized Guest' },
  { value: 'safety_concern', label: 'Safety Concern' },
  { value: 'medical_emergency', label: 'Medical Emergency' },
  { value: 'other', label: 'Other' },
];

const SEVERITY_OPTIONS = [
  { value: 'low', label: 'Low', color: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' },
  { value: 'medium', label: 'Medium', color: 'bg-orange-500/20 text-orange-300 border-orange-500/30' },
  { value: 'high', label: 'High', color: 'bg-red-500/20 text-red-300 border-red-500/30' },
  { value: 'critical', label: 'Critical', color: 'bg-red-900/30 text-red-200 border-red-500/50' },
];

function SeverityBadge({ severity }) {
  const opt = SEVERITY_OPTIONS.find(o => o.value === severity) || SEVERITY_OPTIONS[0];
  return <span className={`px-2 py-0.5 rounded-full text-xs border ${opt.color}`}>{opt.label}</span>;
}

function StatusBadge({ status }) {
  const colors = {
    open: 'bg-red-500/20 text-red-300 border-red-500/30',
    investigating: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
    resolved: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
    positive: 'bg-red-500/20 text-red-300',
    negative: 'bg-emerald-500/20 text-emerald-300',
    compliant: 'bg-emerald-500/20 text-emerald-300',
    violation: 'bg-red-500/20 text-red-300',
  };
  return <span className={`px-2 py-0.5 rounded-full text-xs border ${colors[status] || 'bg-slate-500/20 text-slate-300 border-slate-500/30'}`}>{status?.replace('_', ' ')?.toUpperCase()}</span>;
}

export default function IncidentCompliancePage() {
  const { user, token } = useAuth();
  const [houses, setHouses] = useState([]);
  const [selectedHouse, setSelectedHouse] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [drugTests, setDrugTests] = useState([]);
  const [curfewLogs, setCurfewLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(null);
  const [form, setForm] = useState({});
  const [residents, setResidents] = useState([]);

  const headers = { 'Content-Type': 'application/json' };

  useEffect(() => {
    loadHouses();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadHouses = async () => {
    try {
      const res = await fetch(`${API}/api/housing/houses`, { headers, credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const h = data.houses || data || [];
        setHouses(h);
        if (h.length > 0) {
          setSelectedHouse(h[0]);
          loadHouseData(h[0].id);
        }
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const loadHouseData = async (houseId) => {
    setLoading(true);
    try {
      const [incRes, dtRes, cfRes, resRes] = await Promise.all([
        fetch(`${API}/api/housing/incidents?house_id=${houseId}`, { headers, credentials: 'include' }),
        fetch(`${API}/api/housing/drug-tests?house_id=${houseId}`, { headers, credentials: 'include' }),
        fetch(`${API}/api/housing/curfew-logs?house_id=${houseId}`, { headers, credentials: 'include' }),
        fetch(`${API}/api/housing/houses/${houseId}/residents`, { headers, credentials: 'include' }),
      ]);
      if (incRes.ok) {
        const incData = await incRes.json();
        setIncidents(Array.isArray(incData) ? incData : incData.incidents || []);
      }
      if (dtRes.ok) {
        const dtData = await dtRes.json();
        setDrugTests(Array.isArray(dtData) ? dtData : dtData.drug_tests || []);
      }
      if (cfRes.ok) {
        const cfData = await cfRes.json();
        setCurfewLogs(Array.isArray(cfData) ? cfData : cfData.curfew_logs || []);
      }
      if (resRes.ok) setResidents((await resRes.json()).residents || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleHouseChange = (house) => {
    setSelectedHouse(house);
    loadHouseData(house.id);
  };

  const submitIncident = async () => {
    if (!form.type || !form.description) return;
    try {
      await fetch(`${API}/api/housing/incidents`, { method: 'POST', headers, credentials: 'include',
        body: JSON.stringify({
          house_id: selectedHouse.id,
          incident_type: form.type,
          severity: form.severity || 'medium',
          description: form.description,
          resident_id: form.resident_id || '',
          witnesses: form.witnesses || '',
        }),
      });
      setShowForm(null);
      setForm({});
      loadHouseData(selectedHouse.id);
    } catch (e) { console.error(e); }
  };

  const submitDrugTest = async () => {
    if (!form.resident_id || !form.result) return;
    try {
      await fetch(`${API}/api/housing/drug-tests`, { method: 'POST', headers, credentials: 'include',
        body: JSON.stringify({
          house_id: selectedHouse.id,
          resident_id: form.resident_id,
          result: form.result,
          test_type: 'urine',
          substances_tested: form.substance ? [form.substance] : [],
          notes: form.notes || '',
        }),
      });
      setShowForm(null);
      setForm({});
      loadHouseData(selectedHouse.id);
    } catch (e) { console.error(e); }
  };

  const submitCurfewLog = async () => {
    if (!form.resident_id || !form.status) return;
    try {
      await fetch(`${API}/api/housing/curfew-logs`, { method: 'POST', headers, credentials: 'include',
        body: JSON.stringify({
          house_id: selectedHouse.id,
          resident_id: form.resident_id,
          status: form.status,
          check_in_time: form.check_time || new Date().toISOString(),
          notes: form.notes || '',
        }),
      });
      setShowForm(null);
      setForm({});
      loadHouseData(selectedHouse.id);
    } catch (e) { console.error(e); }
  };

  const resolveIncident = async (incidentId) => {
    try {
      await fetch(`${API}/api/housing/incidents/${incidentId}`, { method: 'PUT', headers, credentials: 'include',
        body: JSON.stringify({ status: 'resolved', resolution: 'Resolved by staff' }),
      });
      loadHouseData(selectedHouse.id);
    } catch (e) { console.error(e); }
  };

  if (loading && houses.length === 0) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-blue-400" /></div>;
  }

  const openIncidents = incidents.filter(i => i.status !== 'resolved').length;
  const positiveDT = drugTests.filter(d => d.result === 'positive').length;
  const curfewViolations = curfewLogs.filter(c => c.status === 'violation').length;

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6" data-testid="incident-compliance-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Shield className="h-8 w-8 text-blue-400" />
          <div>
            <h1 className="text-2xl font-bold text-white">Incident & Compliance</h1>
            <p className="text-slate-400 text-sm">Track incidents, drug tests, and curfew compliance</p>
          </div>
        </div>
      </div>

      {/* House Selector */}
      <div className="flex gap-2 flex-wrap">
        {houses.map(h => (
          <Button
            key={h.id}
            size="sm"
            data-testid={`house-btn-${h.id}`}
            variant={selectedHouse?.id === h.id ? 'default' : 'outline'}
            className={selectedHouse?.id === h.id ? 'bg-blue-600' : 'border-slate-600 text-slate-300'}
            onClick={() => handleHouseChange(h)}
          >
            <Home className="h-3 w-3 mr-1" /> {h.name}
          </Button>
        ))}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="rounded-xl border border-slate-700/50 bg-slate-800/60 p-3 text-center" data-testid="stat-incidents">
          <p className="text-2xl font-bold text-red-400">{openIncidents}</p>
          <p className="text-xs text-slate-400">Open Incidents</p>
        </div>
        <div className="rounded-xl border border-slate-700/50 bg-slate-800/60 p-3 text-center" data-testid="stat-drug-tests">
          <p className="text-2xl font-bold text-yellow-400">{positiveDT}</p>
          <p className="text-xs text-slate-400">Positive Drug Tests</p>
        </div>
        <div className="rounded-xl border border-slate-700/50 bg-slate-800/60 p-3 text-center" data-testid="stat-curfew">
          <p className="text-2xl font-bold text-orange-400">{curfewViolations}</p>
          <p className="text-xs text-slate-400">Curfew Violations</p>
        </div>
        <div className="rounded-xl border border-slate-700/50 bg-slate-800/60 p-3 text-center" data-testid="stat-residents">
          <p className="text-2xl font-bold text-emerald-400">{residents.length}</p>
          <p className="text-xs text-slate-400">Current Residents</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="incidents" className="space-y-4">
        <TabsList className="bg-slate-800/60 border border-slate-700/50">
          <TabsTrigger value="incidents" className="data-[state=active]:bg-slate-700">
            <AlertTriangle className="h-4 w-4 mr-1" /> Incidents ({incidents.length})
          </TabsTrigger>
          <TabsTrigger value="drug-tests" className="data-[state=active]:bg-slate-700">
            <FlaskConical className="h-4 w-4 mr-1" /> Drug Tests ({drugTests.length})
          </TabsTrigger>
          <TabsTrigger value="curfew" className="data-[state=active]:bg-slate-700">
            <Clock className="h-4 w-4 mr-1" /> Curfew ({curfewLogs.length})
          </TabsTrigger>
        </TabsList>

        {/* INCIDENTS TAB */}
        <TabsContent value="incidents">
          <div className="flex justify-end mb-3">
            <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={() => { setShowForm('incident'); setForm({ severity: 'medium' }); }} data-testid="new-incident-btn">
              <Plus className="h-4 w-4 mr-1" /> Report Incident
            </Button>
          </div>

          {showForm === 'incident' && (
            <Card className="bg-slate-800/60 border-red-500/30 mb-4">
              <CardContent className="pt-4 space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <select value={form.type || ''} onChange={e => setForm(p => ({ ...p, type: e.target.value }))} className="px-3 py-2 rounded-lg bg-slate-900 border border-slate-600 text-white text-sm">
                    <option value="">Select Type...</option>
                    {INCIDENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  <select value={form.severity || 'medium'} onChange={e => setForm(p => ({ ...p, severity: e.target.value }))} className="px-3 py-2 rounded-lg bg-slate-900 border border-slate-600 text-white text-sm">
                    {SEVERITY_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
                <select value={form.resident_id || ''} onChange={e => setForm(p => ({ ...p, resident_id: e.target.value }))} className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-600 text-white text-sm">
                  <option value="">Involved Resident (optional)...</option>
                  {residents.map(r => <option key={r.id} value={r.id}>{r.full_name}</option>)}
                </select>
                <textarea value={form.description || ''} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Describe the incident..." rows={3} className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-600 text-white text-sm resize-none" />
                <input type="text" value={form.witnesses || ''} onChange={e => setForm(p => ({ ...p, witnesses: e.target.value }))} placeholder="Witnesses (optional)" className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-600 text-white text-sm" />
                <div className="flex gap-2">
                  <Button size="sm" className="bg-red-600" onClick={submitIncident} data-testid="submit-incident-btn">Submit Report</Button>
                  <Button size="sm" variant="ghost" onClick={() => setShowForm(null)}>Cancel</Button>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="space-y-2">
            {incidents.length === 0 && <p className="text-slate-500 text-center py-8">No incidents reported</p>}
            {incidents.map(inc => (
              <Card key={inc.id} className="bg-slate-800/60 border-slate-700/50" data-testid={`incident-${inc.id}`}>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <AlertTriangle className="h-4 w-4 text-red-400" />
                        <span className="text-white font-medium text-sm">{INCIDENT_TYPES.find(t => t.value === inc.type)?.label || inc.type}</span>
                        <SeverityBadge severity={inc.severity} />
                        <StatusBadge status={inc.status} />
                      </div>
                      <p className="text-sm text-slate-300 mt-1">{inc.description}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        {inc.reporter_name && `Reported by ${inc.reporter_name} `}
                        {new Date(inc.created_at).toLocaleString()}
                      </p>
                    </div>
                    {inc.status !== 'resolved' && (
                      <Button size="sm" variant="ghost" className="text-emerald-400 shrink-0" onClick={() => resolveIncident(inc.id)}>
                        <CheckCircle2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* DRUG TESTS TAB */}
        <TabsContent value="drug-tests">
          <div className="flex justify-end mb-3">
            <Button size="sm" className="bg-purple-600 hover:bg-purple-700" onClick={() => { setShowForm('drug-test'); setForm({}); }} data-testid="new-drug-test-btn">
              <Plus className="h-4 w-4 mr-1" /> Record Drug Test
            </Button>
          </div>

          {showForm === 'drug-test' && (
            <Card className="bg-slate-800/60 border-purple-500/30 mb-4">
              <CardContent className="pt-4 space-y-3">
                <select value={form.resident_id || ''} onChange={e => setForm(p => ({ ...p, resident_id: e.target.value }))} className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-600 text-white text-sm">
                  <option value="">Select Resident...</option>
                  {residents.map(r => <option key={r.id} value={r.id}>{r.full_name}</option>)}
                </select>
                <div className="grid grid-cols-2 gap-2">
                  <select value={form.result || ''} onChange={e => setForm(p => ({ ...p, result: e.target.value }))} className="px-3 py-2 rounded-lg bg-slate-900 border border-slate-600 text-white text-sm">
                    <option value="">Result...</option>
                    <option value="negative">Negative (Clean)</option>
                    <option value="positive">Positive</option>
                  </select>
                  <input type="text" value={form.substance || ''} onChange={e => setForm(p => ({ ...p, substance: e.target.value }))} placeholder="Substance (if positive)" className="px-3 py-2 rounded-lg bg-slate-900 border border-slate-600 text-white text-sm" />
                </div>
                <input type="text" value={form.notes || ''} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Notes..." className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-600 text-white text-sm" />
                <div className="flex gap-2">
                  <Button size="sm" className="bg-purple-600" onClick={submitDrugTest} data-testid="submit-drug-test-btn">Record</Button>
                  <Button size="sm" variant="ghost" onClick={() => setShowForm(null)}>Cancel</Button>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="space-y-2">
            {drugTests.length === 0 && <p className="text-slate-500 text-center py-8">No drug tests recorded</p>}
            {drugTests.map(dt => (
              <div key={dt.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-800/60 border border-slate-700/50" data-testid={`drug-test-${dt.id}`}>
                <div className="flex items-center gap-3">
                  <FlaskConical className={`h-4 w-4 ${dt.result === 'positive' ? 'text-red-400' : 'text-emerald-400'}`} />
                  <div>
                    <p className="text-white text-sm font-medium">{dt.resident_name || 'Unknown'}</p>
                    <p className="text-xs text-slate-500">{new Date(dt.test_date || dt.created_at).toLocaleDateString()} {dt.substance && `| ${dt.substance}`}</p>
                  </div>
                </div>
                <StatusBadge status={dt.result} />
              </div>
            ))}
          </div>
        </TabsContent>

        {/* CURFEW TAB */}
        <TabsContent value="curfew">
          <div className="flex justify-end mb-3">
            <Button size="sm" className="bg-orange-600 hover:bg-orange-700" onClick={() => { setShowForm('curfew'); setForm({}); }} data-testid="new-curfew-btn">
              <Plus className="h-4 w-4 mr-1" /> Log Curfew Check
            </Button>
          </div>

          {showForm === 'curfew' && (
            <Card className="bg-slate-800/60 border-orange-500/30 mb-4">
              <CardContent className="pt-4 space-y-3">
                <select value={form.resident_id || ''} onChange={e => setForm(p => ({ ...p, resident_id: e.target.value }))} className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-600 text-white text-sm">
                  <option value="">Select Resident...</option>
                  {residents.map(r => <option key={r.id} value={r.id}>{r.full_name}</option>)}
                </select>
                <select value={form.status || ''} onChange={e => setForm(p => ({ ...p, status: e.target.value }))} className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-600 text-white text-sm">
                  <option value="">Status...</option>
                  <option value="compliant">Compliant (Present)</option>
                  <option value="violation">Violation (Absent/Late)</option>
                </select>
                <input type="text" value={form.notes || ''} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Notes..." className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-600 text-white text-sm" />
                <div className="flex gap-2">
                  <Button size="sm" className="bg-orange-600" onClick={submitCurfewLog} data-testid="submit-curfew-btn">Log</Button>
                  <Button size="sm" variant="ghost" onClick={() => setShowForm(null)}>Cancel</Button>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="space-y-2">
            {curfewLogs.length === 0 && <p className="text-slate-500 text-center py-8">No curfew logs recorded</p>}
            {curfewLogs.map(cl => (
              <div key={cl.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-800/60 border border-slate-700/50" data-testid={`curfew-${cl.id}`}>
                <div className="flex items-center gap-3">
                  <Clock className={`h-4 w-4 ${cl.status === 'compliant' ? 'text-emerald-400' : 'text-red-400'}`} />
                  <div>
                    <p className="text-white text-sm font-medium">{cl.resident_name || 'Unknown'}</p>
                    <p className="text-xs text-slate-500">{new Date(cl.check_time || cl.created_at).toLocaleString()} {cl.notes && `| ${cl.notes}`}</p>
                  </div>
                </div>
                <StatusBadge status={cl.status} />
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
